import { Flex } from 'foundations-components/components/Flex';
import { useIsMobile } from 'pricing-pages-ui-components/hooks/useMedia';
import MerchCardCTAOverridesWrapper from '../../../../features/purchaseMotions/components/MerchCardCTAOverridesWrapper';
import { TalkToSalesFallback } from '../../../../features/purchaseMotions/components/TalkToSalesFallback';
import { useGlobalLoading } from '../../../../hooks/loadingHooks';
import { useAppSelector } from '../../../../reducers';
import { packagedQuantityBySkuIdSelector, productSelector, quantityPackForProductVersionSelector } from '../../../../selectors/ProductsSelectors';
import { useQueryParamTerm } from '../../../../utils/urls/appQueryParamUtilsLegacy';
import { MerchCardLayoutUI } from '../../../../v2/components/MerchCardLayoutUI';
import { ErrorBoundary } from '../../../../v2/components/error-boundary/ErrorBoundary';
import { IncludedHubSpotCredits } from '../../../../v2/components/merch-card/credits-display/IncludedHubSpotCredits';
import { IncludedContacts } from '../../../../v2/components/merch-card/subtext-display/IncludedContacts';
import { MERCH_CARD } from '../../../../v2/constants/tracking/TrackingSources';
import { MerchCardSubheadingContainer } from '../../../../v2/containers/merch-card/headings-display/MerchCardSubheadingContainer';
import { MerchCardTitleContainer } from '../../../../v2/containers/merch-card/headings-display/MerchCardTitleContainer';
import { default as DetailedTermSelector } from '../../../common/TermSelectors/DetailedTermSelector';
import { default as SkeletonLoadWrapper } from '../../../common/loading/SkeletonLoadWrapper';
import RequiredOnboardingSubtextContainer from '../../../plan-builder/RequiredOnboardingSubtextContainer';
import { SuiteFeatures } from '../../features-display/SuiteFeatures';
import { ProductTileFeaturesContainer } from '../../features-display/features-display-containers/ProductTileFeaturesContainer';
import { PriceDisplayContainer } from '../../price-display/PriceDisplayContainer';
import { IncludedSeatsDisplayContainerV1 } from '../../seats-display/IncludedSeatsDisplayContainerV1';
import { ProductCardCTAs } from './ProductCardCTAs';
import { useProductCardMetadata } from './useProductCard';
import { jsx as _jsx } from "react/jsx-runtime";
export const ProductCard = ({
  apiName,
  screen,
  trackingSource,
  columnStart,
  Title,
  Description,
  Badge,
  skipInterstitialPage = false
}) => {
  const isAppReady = useGlobalLoading();
  const isMobile = useIsMobile();
  const product = useAppSelector(state => {
    if (!isAppReady) return;
    return productSelector(state, {
      apiName
    });
  });
  const {
    skuId,
    productLevel,
    shouldShowTermSelector,
    purchaseMotions,
    isHubspotFree,
    shouldShowSuiteObjectives
  } = useProductCardMetadata({
    apiName
  });
  const includedProductLevelHubspotCredits = useAppSelector(state => {
    if (!skuId) return null;
    return packagedQuantityBySkuIdSelector(state, {
      skuId,
      unitName: 'HUBSPOT_CREDITS'
    });
  });
  const quantityPack = useAppSelector(state => {
    if (!skuId) return null;
    return quantityPackForProductVersionSelector(state, {
      productVersionSkuId: skuId,
      quantityPackApiName: 'additional-marketing-contacts'
    });
  });
  const additionalUnitSkuId = quantityPack === null || quantityPack === void 0 ? void 0 : quantityPack.skuId;
  const includedContacts = useAppSelector(state => {
    if (!skuId) return 0;
    return packagedQuantityBySkuIdSelector(state, {
      skuId,
      unitName: 'CONTACTS'
    });
  });
  const {
    setQueryParamTerm,
    queryParamTerm
  } = useQueryParamTerm();
  if (!isAppReady || !product || !apiName || !skuId) {
    return null;
  }
  const TitleComponent = /*#__PURE__*/_jsx(MerchCardTitleContainer, {
    Title: Title
  });
  const Term = shouldShowTermSelector && /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
    isLoading: !isAppReady,
    heights: [41],
    widths: ['100%'],
    children: /*#__PURE__*/_jsx(DetailedTermSelector, {
      apiName: apiName,
      onChange: setQueryParamTerm,
      activeTerm: queryParamTerm,
      trackingSource: trackingSource
    })
  });
  const Subtext = additionalUnitSkuId ? /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
    isLoading: !isAppReady,
    heights: [17],
    widths: ['75%'],
    children: /*#__PURE__*/_jsx(IncludedContacts, {
      additionalUnitSkuId: additionalUnitSkuId,
      includedWithParentProduct: includedContacts,
      productLevel: productLevel,
      queryParamTerm: queryParamTerm
    })
  }) : null;
  const Credits = /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
    isLoading: !isAppReady,
    heights: [17],
    widths: ['75%'],
    children: !!includedProductLevelHubspotCredits && /*#__PURE__*/_jsx(IncludedHubSpotCredits, {
      includedProductLevelHubspotCredits: includedProductLevelHubspotCredits,
      trackingSource: trackingSource
    })
  });
  const CTAs = /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
    isLoading: !isAppReady,
    heights: [47],
    widths: ['100%'],
    children: /*#__PURE__*/_jsx(ErrorBoundary, {
      fallback: /*#__PURE__*/_jsx(TalkToSalesFallback, {
        apiName: apiName,
        screen: screen,
        trackingSource: trackingSource
      }),
      children: /*#__PURE__*/_jsx(MerchCardCTAOverridesWrapper, {
        skuId: skuId,
        trackingSource: MERCH_CARD,
        children: /*#__PURE__*/_jsx(Flex, {
          flexDirection: "column",
          justifyContent: "center",
          gap: "200",
          inlineSize: "100%",
          children: /*#__PURE__*/_jsx(ProductCardCTAs, {
            apiName: apiName,
            screen: screen,
            trackingSource: trackingSource,
            skipInterstitialPage: skipInterstitialPage,
            isHubspotFree: isHubspotFree,
            purchaseMotions: purchaseMotions
          })
        })
      })
    })
  });
  const Features = /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
    isLoading: !isAppReady,
    heights: [48, 48],
    widths: ['100%', '100%'],
    children: shouldShowSuiteObjectives ? /*#__PURE__*/_jsx(SuiteFeatures, {
      productLevel: productLevel,
      loading: !isAppReady,
      isCollapsible: isMobile
    }) : /*#__PURE__*/_jsx(ProductTileFeaturesContainer, {
      apiName: apiName,
      isCollapsible: isMobile
    })
  });
  const Footer = skuId && /*#__PURE__*/_jsx(Flex, {
    flexDirection: "column",
    alignItems: "center",
    children: /*#__PURE__*/_jsx(RequiredOnboardingSubtextContainer, {
      queryParamTerm: queryParamTerm,
      skuIds: [skuId],
      trackingSource: trackingSource,
      includesOnboardingCost: false
    })
  });
  return /*#__PURE__*/_jsx(MerchCardLayoutUI, {
    "data-selenium-id": `${apiName}-merch-card`,
    "data-test-id": `${apiName}-merch-card`,
    columnStart: columnStart,
    Badge: Badge,
    Title: TitleComponent,
    Subheading: /*#__PURE__*/_jsx(MerchCardSubheadingContainer, {
      Subheading: Description || null,
      apiName: apiName
    }),
    Pricing: /*#__PURE__*/_jsx(PriceDisplayContainer, {
      skuId: skuId,
      queryParamTerm: queryParamTerm,
      apiName: apiName,
      isHubspotFree: isHubspotFree
    }),
    Seats: /*#__PURE__*/_jsx(IncludedSeatsDisplayContainerV1, {
      skuId: skuId
    }),
    Term: Term,
    Subtext: Subtext,
    Credits: Credits,
    CTAs: CTAs,
    Features: Features,
    Footer: Footer
  });
};